import { useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Loader2, CheckCircle2, Upload, Square } from "lucide-react";
import api from "@/lib/api";

export default function CharacterVoice({ characterId, hasVoice, onDone }) {
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [secs, setSecs] = useState(0);
  const ref = useRef(null);
  const recRef = useRef(null);
  const chunks = useRef([]);
  const tick = useRef(null);

  const uploadBlob = async (blob, name) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, name);
      await api.post(`/characters/${characterId}/voice`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Voz del personaje guardada");
      await onDone?.();
    } catch {
      toast.error("No se pudo guardar la voz");
    } finally { setBusy(false); }
  };

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (f) await uploadBlob(f, f.name);
    e.target.value = "";
  };

  const stopRec = () => {
    try { if (recRef.current && recRef.current.state !== "inactive") recRef.current.stop(); } catch { /* noop */ }
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunks.current = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(tick.current);
        setRecording(false);
        setSecs(0);
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        if (blob.size > 0) await uploadBlob(blob, "voz-personaje.webm");
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
      setSecs(0);
      tick.current = setInterval(() => setSecs((s) => {
        const n = s + 1;
        if (n >= 15) stopRec();
        return n;
      }), 1000);
    } catch {
      toast.error("No se pudo acceder al micrófono. Da permiso e inténtalo de nuevo.");
    }
  };

  return (
    <div className="mfp-elev rounded-xl p-3 flex items-center gap-3" data-testid={`character-voice-${characterId}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${hasVoice ? "bg-emerald-500/15" : recording ? "bg-[#FF334B]/20 animate-pulse" : "bg-[#FF334B]/12"}`}>
        {hasVoice ? <CheckCircle2 className="w-4.5 h-4.5 text-emerald-400" /> : <Mic className="w-4.5 h-4.5 text-[#FF334B]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-head font-semibold text-xs">
          {recording ? `Grabando… ${secs}s / 15s` : hasVoice ? "Voz autorizada lista" : "Voz autorizada"}
        </div>
        <div className="text-[11px] text-[#64748B] truncate">
          {recording ? "Habla claro; se detiene sola a los 15s" : "Graba o sube la voz de este personaje"}
        </div>
      </div>
      <input ref={ref} type="file" accept="audio/*" onChange={pick} className="hidden" data-testid={`character-voice-input-${characterId}`} />
      <div className="flex items-center gap-1.5 shrink-0">
        {recording ? (
          <button data-testid={`character-voice-stop-${characterId}`} onClick={stopRec}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#FF334B] text-white hover:bg-[#e02a40] transition-colors">
            <Square className="w-3.5 h-3.5" /> Detener
          </button>
        ) : (
          <button data-testid={`character-voice-record-${characterId}`} onClick={startRec} disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10 transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Mic className="w-3.5 h-3.5" />} Grabar
          </button>
        )}
        <button data-testid={`character-voice-upload-${characterId}`} onClick={() => ref.current?.click()} disabled={busy || recording}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-50">
          <Upload className="w-3.5 h-3.5" /> {hasVoice ? "Cambiar" : "Subir"}
        </button>
      </div>
    </div>
  );
}
